import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Camera, Upload, MapPin, Navigation, CheckCircle, AlertTriangle,
  User, Home, Phone, Image, Clock, Trash2, Eye, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Photo types for B2B Address Verification
const PHOTO_TYPES = [
  { id: 'fe_selfie', label: 'Field Executive Selfie', required: true, description: 'Your selfie at the location' },
  { id: 'house_photo_1', label: 'House Photo 1', required: true, description: 'Front view of the residence' },
  { id: 'house_photo_2', label: 'House Photo 2', required: true, description: 'Another angle of the house' },
  { id: 'landmark_photo_1', label: 'Landmark Photo 1', required: true, description: 'Nearby landmark for reference' },
  { id: 'landmark_photo_2', label: 'Landmark Photo 2', required: false, description: 'Another landmark (optional)' },
  { id: 'id_proof', label: 'Verifier ID Proof', required: true, description: 'Your ID card/proof' }
];

const RESIDENCE_STATUS_OPTIONS = [
  'Own House', 'Rented House', 'Paying Guest', 'Company Accommodation',
  'Hostel', "Relative's House", 'Other'
];

export default function B2BFieldVerification() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  
  // Camera state
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [captureTarget, setCaptureTarget] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    verifier_name: localStorage.getItem('b2b_fe_name') || '',
    verifier_contact: localStorage.getItem('b2b_fe_phone') || '',
    date_of_visit: new Date().toISOString().split('T')[0],
    time_of_visit: new Date().toTimeString().slice(0, 5),
    gps_coordinates: '',
    residence_confirmed: 'yes',
    residence_status: '',
    period_of_stay: '',
    person_met_name: '',
    person_met_relation: '',
    person_met_contact: '',
    neighbour_1: { name: '', address: '', phone: '', relationship: '' },
    neighbour_2: { name: '', address: '', phone: '', relationship: '' },
    overall_remarks: '',
    ethics_declaration: false
  });

  // Load case data
  useEffect(() => {
    const fetchCase = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/b2b/cases/${caseId}/field-form`);
        setCaseData(res.data);
      } catch (error) {
        toast.error('Failed to load case details');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    
    if (caseId) {
      fetchCase();
    }
  }, [caseId]);

  // Get GPS location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setCurrentLocation(coords);
          setForm(prev => ({
            ...prev,
            gps_coordinates: `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`
          }));
        },
        (error) => {
          setLocationError(error.message);
        },
        { enableHighAccuracy: true }
      );
    }
  }, []);

  // Camera functions
  const startCamera = async (photoType) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: photoType === 'fe_selfie' ? 'user' : 'environment' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCapturing(true);
      setCaptureTarget(photoType);
    } catch (error) {
      toast.error('Failed to access camera');
    }
  };

  const capturePhoto = () => {
    if (!canvasRef.current || !videoRef.current) return;
    
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    
    // Add photo with GPS data
    const photoData = {
      id: `${captureTarget}_${Date.now()}`,
      type: captureTarget,
      label: PHOTO_TYPES.find(p => p.id === captureTarget)?.label || captureTarget,
      url: dataUrl,
      base64: dataUrl.split(',')[1],
      latitude: currentLocation?.latitude,
      longitude: currentLocation?.longitude,
      location_name: caseData?.city || '',
      timestamp: new Date().toISOString()
    };
    
    setPhotos(prev => [...prev.filter(p => p.type !== captureTarget), photoData]);
    stopCamera();
    toast.success('Photo captured!');
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setCapturing(false);
    setCaptureTarget(null);
  };

  const removePhoto = (photoType) => {
    setPhotos(prev => prev.filter(p => p.type !== photoType));
  };

  // File upload handler
  const handleFileUpload = (event, photoType) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const photoData = {
        id: `${photoType}_${Date.now()}`,
        type: photoType,
        label: PHOTO_TYPES.find(p => p.id === photoType)?.label || photoType,
        url: reader.result,
        base64: reader.result.split(',')[1],
        latitude: currentLocation?.latitude,
        longitude: currentLocation?.longitude,
        location_name: caseData?.city || '',
        timestamp: new Date().toISOString()
      };
      
      setPhotos(prev => [...prev.filter(p => p.type !== photoType), photoData]);
      toast.success('Photo uploaded!');
    };
    reader.readAsDataURL(file);
  };

  // Submit verification
  const handleSubmit = async () => {
    // Validate ethics
    if (!form.ethics_declaration) {
      toast.error('Please accept the ethics declaration');
      return;
    }
    
    // Validate required photos
    const requiredPhotos = PHOTO_TYPES.filter(p => p.required);
    const uploadedTypes = photos.map(p => p.type);
    const missingPhotos = requiredPhotos.filter(p => !uploadedTypes.includes(p.id));
    
    if (missingPhotos.length > 0) {
      toast.error(`Please upload required photos: ${missingPhotos.map(p => p.label).join(', ')}`);
      return;
    }
    
    if (!form.verifier_name) {
      toast.error('Please enter your name');
      return;
    }
    
    // Save FE details locally
    localStorage.setItem('b2b_fe_name', form.verifier_name);
    localStorage.setItem('b2b_fe_phone', form.verifier_contact);
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/b2b/cases/${caseId}/field-verification/submit`, {
        ...form,
        photos: photos,
        submitted_at: new Date().toISOString()
      });
      toast.success('Verification submitted successfully!');
      
      // Show success screen
      setTimeout(() => {
        navigate('/b2b-field-success');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  const getPhotoStatus = (photoType) => {
    return photos.find(p => p.type === photoType);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Clock className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading verification details...</p>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Case Not Found</h2>
            <p className="text-slate-600">The verification case could not be found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold">JustVerified</h1>
                <p className="text-indigo-100 text-sm">B2B Field Verification</p>
              </div>
              <Home className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>

        {/* Case Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              Candidate Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-slate-500">Case ID:</span>
                <p className="font-medium">{caseData.internal_case_id}</p>
              </div>
              <div>
                <span className="text-slate-500">Name:</span>
                <p className="font-medium">{caseData.candidate_name}</p>
              </div>
              <div>
                <span className="text-slate-500">Phone:</span>
                <p className="font-medium">{caseData.phone || 'N/A'}</p>
              </div>
              <div>
                <span className="text-slate-500">Check Type:</span>
                <Badge variant="secondary">{caseData.check_type?.replace('_pv', '').replace('_', ' ')}</Badge>
              </div>
            </div>
            <div className="border-t pt-2 mt-2">
              <span className="text-slate-500 text-sm">Address to Verify:</span>
              <p className="font-medium">{caseData.address}, {caseData.city}, {caseData.state} - {caseData.pincode}</p>
            </div>
          </CardContent>
        </Card>

        {/* GPS Location */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentLocation ? 'bg-green-100' : 'bg-amber-100'}`}>
                <MapPin className={`w-5 h-5 ${currentLocation ? 'text-green-600' : 'text-amber-600'}`} />
              </div>
              <div className="flex-1">
                <p className="font-medium">{currentLocation ? 'Location Captured' : 'Getting Location...'}</p>
                <p className="text-sm text-slate-500">{form.gps_coordinates || locationError || 'Please enable GPS'}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Verifier Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Field Executive Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Your Name *</Label>
              <Input
                value={form.verifier_name}
                onChange={(e) => setForm({...form, verifier_name: e.target.value})}
                placeholder="Enter your full name"
              />
            </div>
            <div>
              <Label>Your Contact Number</Label>
              <Input
                value={form.verifier_contact}
                onChange={(e) => setForm({...form, verifier_contact: e.target.value})}
                placeholder="Your phone number"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date of Visit</Label>
                <Input
                  type="date"
                  value={form.date_of_visit}
                  onChange={(e) => setForm({...form, date_of_visit: e.target.value})}
                />
              </div>
              <div>
                <Label>Time of Visit</Label>
                <Input
                  type="time"
                  value={form.time_of_visit}
                  onChange={(e) => setForm({...form, time_of_visit: e.target.value})}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Photo Capture */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="w-5 h-5 text-indigo-600" />
              Photo Evidence
            </CardTitle>
          </CardHeader>
          <CardContent>
            {capturing && (
              <div className="mb-4 relative">
                <video ref={videoRef} autoPlay playsInline className="w-full rounded-lg" />
                <canvas ref={canvasRef} className="hidden" />
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                  <Button onClick={capturePhoto} className="bg-white text-indigo-600 hover:bg-indigo-50">
                    <Camera className="w-5 h-5 mr-2" /> Capture
                  </Button>
                  <Button variant="destructive" onClick={stopCamera}>Cancel</Button>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              {PHOTO_TYPES.map((photoType) => {
                const existingPhoto = getPhotoStatus(photoType.id);
                return (
                  <div
                    key={photoType.id}
                    className={`border rounded-lg p-3 ${existingPhoto ? 'border-green-300 bg-green-50' : 'border-dashed border-slate-300'}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{photoType.label}</span>
                      {photoType.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                    </div>
                    
                    {existingPhoto ? (
                      <div className="relative">
                        <img src={existingPhoto.url} alt={photoType.label} className="w-full h-24 object-cover rounded" />
                        <Button
                          size="sm"
                          variant="destructive"
                          className="absolute top-1 right-1 w-6 h-6 p-0"
                          onClick={() => removePhoto(photoType.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                        <CheckCircle className="absolute bottom-1 left-1 w-5 h-5 text-green-600" />
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => startCamera(photoType.id)}
                        >
                          <Camera className="w-4 h-4" />
                        </Button>
                        <label className="flex-1">
                          <Button size="sm" variant="outline" className="w-full" asChild>
                            <span><Upload className="w-4 h-4" /></span>
                          </Button>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e, photoType.id)}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Verification Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Verification Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Residence Confirmed</Label>
              <Select value={form.residence_confirmed} onValueChange={(v) => setForm({...form, residence_confirmed: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes - Candidate resides here</SelectItem>
                  <SelectItem value="no">No - Candidate does not reside</SelectItem>
                  <SelectItem value="unknown">Unable to verify</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Residence Status</Label>
              <Select value={form.residence_status} onValueChange={(v) => setForm({...form, residence_status: v})}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  {RESIDENCE_STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Period of Stay</Label>
              <Input
                value={form.period_of_stay}
                onChange={(e) => setForm({...form, period_of_stay: e.target.value})}
                placeholder="e.g., 2 years, Since 2020"
              />
            </div>
            
            <div className="border-t pt-4">
              <Label className="text-base font-medium">Person Met</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-sm">Name</Label>
                  <Input
                    value={form.person_met_name}
                    onChange={(e) => setForm({...form, person_met_name: e.target.value})}
                    placeholder="Name"
                  />
                </div>
                <div>
                  <Label className="text-sm">Relation</Label>
                  <Input
                    value={form.person_met_relation}
                    onChange={(e) => setForm({...form, person_met_relation: e.target.value})}
                    placeholder="Relation to candidate"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-sm">Contact</Label>
                  <Input
                    value={form.person_met_contact}
                    onChange={(e) => setForm({...form, person_met_contact: e.target.value})}
                    placeholder="Phone number"
                  />
                </div>
              </div>
            </div>
            
            {/* Neighbour 1 */}
            <div className="border-t pt-4">
              <Label className="text-base font-medium">Neighbour 1 Information</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-sm">Name</Label>
                  <Input
                    value={form.neighbour_1.name}
                    onChange={(e) => setForm({...form, neighbour_1: {...form.neighbour_1, name: e.target.value}})}
                    placeholder="Neighbour name"
                  />
                </div>
                <div>
                  <Label className="text-sm">Phone</Label>
                  <Input
                    value={form.neighbour_1.phone}
                    onChange={(e) => setForm({...form, neighbour_1: {...form.neighbour_1, phone: e.target.value}})}
                    placeholder="Phone"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-sm">Address</Label>
                  <Input
                    value={form.neighbour_1.address}
                    onChange={(e) => setForm({...form, neighbour_1: {...form.neighbour_1, address: e.target.value}})}
                    placeholder="Neighbour address"
                  />
                </div>
              </div>
            </div>
            
            {/* Neighbour 2 */}
            <div className="border-t pt-4">
              <Label className="text-base font-medium">Neighbour 2 Information</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-sm">Name</Label>
                  <Input
                    value={form.neighbour_2.name}
                    onChange={(e) => setForm({...form, neighbour_2: {...form.neighbour_2, name: e.target.value}})}
                    placeholder="Neighbour name"
                  />
                </div>
                <div>
                  <Label className="text-sm">Phone</Label>
                  <Input
                    value={form.neighbour_2.phone}
                    onChange={(e) => setForm({...form, neighbour_2: {...form.neighbour_2, phone: e.target.value}})}
                    placeholder="Phone"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-sm">Address</Label>
                  <Input
                    value={form.neighbour_2.address}
                    onChange={(e) => setForm({...form, neighbour_2: {...form.neighbour_2, address: e.target.value}})}
                    placeholder="Neighbour address"
                  />
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <Label>Overall Remarks</Label>
              <Textarea
                value={form.overall_remarks}
                onChange={(e) => setForm({...form, overall_remarks: e.target.value})}
                placeholder="Any additional observations or remarks..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Ethics Declaration */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="ethics"
                checked={form.ethics_declaration}
                onCheckedChange={(checked) => setForm({...form, ethics_declaration: checked})}
              />
              <label htmlFor="ethics" className="text-sm text-slate-600 cursor-pointer">
                I hereby declare that all information provided is accurate to the best of my knowledge. 
                I have personally visited the location and collected the evidence as per verification guidelines.
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          size="lg"
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Clock className="w-5 h-5 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              Submit Verification
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
